import { Alert, Button, Group, Image, PasswordInput, Stack, TextInput, Title } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { getHotkeyHandler } from '@mantine/hooks'
import { IconAlertOctagon, IconCircleCheck } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { usePostHog } from 'posthog-js/react'
import { parse } from 'qs'
import { useMemo, useState } from 'react'
import { useSearch } from 'wouter'
import { z } from 'zod'

import logoImage from '@/assets/images/logo-full-light-punchout.png'
import { useGlobalState } from '@/hooks/useGlobalState'
import { navigateTo } from '@/utils/navigation'

const LoginSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  password: z.string().min(6, 'Must be at least 6 characters long'),
})

export const LoginScreen = observer(function LoginScreen() {
  const posthog = usePostHog()
  const searchParamsString = useSearch()
  const searchParams: { email?: string; channelDeleted?: string; channelName?: string } = parse(searchParamsString)

  const { authenticationStore, uiStore } = useGlobalState()
  const [authError, setAuthError] = useState<string | null>(null)
  const [channelDeletedNotice, setChannelDeletedNotice] = useState<{ name: string } | null>(() =>
    searchParams.channelDeleted === '1' ? { name: (searchParams.channelName ?? '').trim() } : null,
  )

  const form = useForm<z.infer<typeof LoginSchema>>({
    mode: 'controlled',
    validate: zodResolver(LoginSchema),
    initialValues: {
      email: searchParams.email ?? '',
      password: '',
    },
  })

  const { logIn } = authenticationStore

  const onKeyboardEnter = useMemo(() => getHotkeyHandler([['Enter', () => void submitForm()]]), [])

  async function submitForm() {
    setAuthError(null)

    const validations = form.validate()

    if (validations.hasErrors) {
      setAuthError('Double check your email and password.')
      return
    }

    uiStore.showLoadingOverlay('relative')

    const response = await logIn(form.getValues())

    uiStore.hideLoadingOverlay()

    if (!response.ok) {
      if (response.code === 'NEW_PASSWORD_REQUIRED') {
        navigateTo('changePassword', {
          searchParams: {
            email: form.getValues().email,
            oldPassword: form.getValues().password,
          },
        })
        return
      }

      setAuthError(response.message)
    } else if (response.message === 'Sponsor selection required') {
      // User will be redirected to sponsor selection screen by route logic
      return
    } else {
      posthog?.capture('login_success', { $set: { email: form.getValues().email }, skPlatform: 'portal' })
    }
  }

  return (
    <Stack gap="xxl" w="100%">
      <Image src={logoImage} alt="Sidekick Platform" fit="contain" w="100%" h="auto" />
      <Title order={1} size="h6" ta="center" fw={500}>
        Program Manager and Expert Portal
      </Title>

      {channelDeletedNotice && (
        <Alert
          variant="light"
          color="green"
          icon={<IconCircleCheck />}
          withCloseButton
          onClose={() => setChannelDeletedNotice(null)}
          title={
            channelDeletedNotice.name
              ? `Channel "${channelDeletedNotice.name}" was successfully deleted.`
              : 'Your channel was successfully deleted.'
          }>
          You have been logged out. Sign in to access another channel or contact support if this was unexpected.
        </Alert>
      )}

      {authError && <Alert variant="light" color="red" title={authError} icon={<IconAlertOctagon />}></Alert>}

      <TextInput withAsterisk label="Email Address" onKeyDown={onKeyboardEnter} {...form.getInputProps('email')} />
      <PasswordInput withAsterisk label="Password" onKeyDown={onKeyboardEnter} {...form.getInputProps('password')} />

      <Stack align="center">
        <Button size="lg" onClick={() => void submitForm()} fullWidth>
          Login
        </Button>

        <Group gap="md" justify="center">
          <Button variant="subtle" size="compact-lg" onClick={() => navigateTo('forgotPassword')}>
            Forgot Password?
          </Button>

          <Button
            variant="subtle"
            size="compact-lg"
            onClick={() =>
              navigateTo('changePassword', {
                searchParams: form.getValues().email ? { email: form.getValues().email } : null,
              })
            }>
            Change Password
          </Button>
        </Group>
      </Stack>
    </Stack>
  )
})
